import React, { useState } from 'react'

const COURSE_DISPLAY = {
  Python: { name: 'Intro to Programming in Python', code: 'CS 1064' },
  C:      { name: 'Introduction to Programming in C', code: 'CS 1044' },
}

export default function DeploymentCard({ deployment, summary, loading, onClick, onDownload }) {
  const [hovered, setHovered] = useState(false)

  const fmtDur = (sec) => {
    if (!sec) return '—'
    const m = Math.floor(sec / 60), s = sec % 60
    return s > 0 ? `${m}m ${s}s` : `${m}m`
  }

  const courseInfo = COURSE_DISPLAY[deployment.language] || { name: deployment.course, code: deployment.course }

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...styles.card, borderColor: hovered ? '#c0c0c5' : '#e8e8eb', boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.06)' : 'none' }}
    >
      {/* Download button — top right, only on hover when loaded */}
      {summary?.loaded && onDownload && (
        <button
          onClick={(e) => { e.stopPropagation(); onDownload(deployment) }}
          title={`Download all ${summary.studentCount} students' telemetry`}
          style={{
            position: 'absolute', top: '12px', right: '12px', zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '28px', height: '28px', borderRadius: '6px',
            border: '1.5px solid #e0e0e3', background: '#fff',
            cursor: 'pointer', transition: 'all 0.12s',
            opacity: 1,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#ccc' }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e0e0e3' }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.course}>{courseInfo.name}</div>
          <div style={styles.label}>{deployment.label} · {courseInfo.code}</div>
        </div>
        {loading && (
          <div style={styles.loadingDot} />
        )}
      </div>

      {/* Date + time */}
      <div style={styles.meta}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span>{deployment.date} · {deployment.time}</span>
      </div>

      {/* Stats — show if loaded, otherwise show click to load */}
      {summary?.loaded ? (
        <div style={styles.statsGrid}>
          <StatCell value={summary.studentCount} label="students" />
          <StatCell value={`${summary.passRate}%`} label="pass rate" />
          <StatCell value={`${summary.aiUsage}%`} label="AI usage" />
          <StatCell value={fmtDur(summary.avgDuration)} label="avg duration" />
        </div>
      ) : (
        <div style={styles.clickToLoad}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={styles.spinnerSmall} />
              <span>Loading...</span>
            </div>
          ) : (
            <span>Click to load</span>
          )}
        </div>
      )}
    </div>
  )
}

function StatCell({ value, label }) {
  return (
    <div style={styles.statCell}>
      <div style={styles.statValue}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  )
}

const styles = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: '18px 20px',
    borderRadius: '12px',
    border: '1.5px solid #e8e8eb',
    background: '#fff',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    gap: '14px',
    position: 'relative',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  course: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#111',
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
  },
  label: {
    fontSize: '13px',
    color: '#888',
    marginTop: '2px',
  },
  loadingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    animation: 'pulse 1.5s ease-in-out infinite',
    flexShrink: 0,
    marginTop: '6px',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#999',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
    marginTop: '2px',
  },
  statCell: {
    padding: '8px 10px',
    borderRadius: '8px',
    backgroundColor: '#f8f9fa',
  },
  statValue: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#111',
  },
  statLabel: {
    fontSize: '10px',
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginTop: '1px',
  },
  clickToLoad: {
    fontSize: '12px',
    color: '#ccc',
    padding: '16px 0 4px',
  },
  spinnerSmall: {
    width: '12px',
    height: '12px',
    border: '2px solid #e0e0e0',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
}